import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import { styled, keyframes } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../config/authConfig';
import config from '../../config/config';
import ErrorModal from '../../components/ErrorModal';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import BusinessIcon from '@mui/icons-material/Business';
import DomainIcon from '@mui/icons-material/Domain';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

const smoothFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const MyContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100dvh',
  height: '100%',
  width: '100vw',
  overflowX: 'hidden',
  overflowY: 'auto',
  padding: '16px',
  boxSizing: 'border-box',
  backgroundImage: `url(${process.env.PUBLIC_URL}/images/fondo.jpg)`,
  backgroundSize: '100% 100%',
  backgroundPosition: 'center center',
  backgroundRepeat: 'no-repeat',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    backgroundSize: 'cover',
    alignItems: 'center',
    padding: '24px 14px',
  },
  [theme.breakpoints.down('sm')]: {
    alignItems: 'center',
    padding: '16px 12px',
  },
}));

const MyPaper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  padding: 'clamp(22px, 4vh, 34px) clamp(18px, 4vw, 30px)',
  width: '100%',
  maxWidth: '400px',
  minHeight: 'auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '22px',
  border: '1px solid rgba(255, 255, 255, 0.85)',
  boxShadow: '0 20px 45px rgba(0, 0, 0, 0.22)',
  backgroundColor: 'rgba(255, 255, 255, 0.97)',
  backdropFilter: 'blur(16px)',
  boxSizing: 'border-box',
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${smoothFadeIn} 0.25s ease-out`,
  margin: 'auto',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    padding: '26px 18px',
    borderRadius: '18px',
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.18)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    fontSize: '0.88rem',
    minHeight: '44px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)',
    '& fieldset': {
      borderColor: '#cbd5e1',
      borderWidth: '1.5px',
    },
    '&:hover fieldset': {
      borderColor: '#0655A5',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0655A5',
      borderWidth: '2px',
    },
    '& input::placeholder': {
      color: '#94a3b8',
      opacity: 0.85,
      fontSize: '0.86rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.90rem',
      minHeight: '46px',
    },
  },
  '& input::-ms-reveal, & input::-ms-clear': {
    display: 'none',
  },
  '& input::-webkit-contacts-auto-fill-button, & input::-webkit-credentials-auto-fill-button': {
    visibility: 'hidden',
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.85rem',
    color: '#64748b',
    '&.Mui-focused': {
      color: '#0655A5',
      fontWeight: 700,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.88rem',
    },
  }
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#0655A5',
  color: '#ffffff',
  borderRadius: '12px',
  fontWeight: 700,
  fontSize: '0.90rem',
  textTransform: 'none',
  boxShadow: '0 8px 16px rgba(6, 85, 165, 0.25)',
  width: '100%',
  minHeight: '44px',
  padding: '10px 0',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: '#054487',
    boxShadow: '0 10px 20px rgba(6, 85, 165, 0.35)',
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '46px',
    fontSize: '0.92rem',
  },
}));

const MicrosoftButton = styled(Button)({
  width: '100%',
  padding: 'clamp(7px, 1.1vh, 10px) 14px',
  borderRadius: '12px',
  border: '1.5px solid #cbd5e1',
  backgroundColor: '#ffffff',
  color: '#0f172a',
  fontWeight: 700,
  fontSize: 'clamp(0.74rem, 1.1vh, 0.82rem)',
  textTransform: 'none',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '10px',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)',
  marginBottom: 'clamp(6px, 1vh, 10px)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f8fafc',
    borderColor: '#0655A5',
    boxShadow: '0 4px 12px rgba(6, 85, 165, 0.12)',
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
});

const MicrosoftLogo = () => (
  <svg width="18" height="18" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" rx="1"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" rx="1"/>
    <rect x="11" y="1" width="9" height="9" fill="#ffb900" rx="1"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" rx="1"/>
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const { login, verify2fa, resend2fa, loginWithMicrosoft } = useAuth();
  const { instance, accounts } = useMsal();

  // ─── Vista actual: 'LOGIN' | '2FA' | 'COMPANIES' ───
  const [viewMode, setViewMode] = useState('LOGIN');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMsLoading, setIsMsLoading] = useState(false);

  // ─── Estado para 2FA (6 Casillas OTP) ───
  const [temp2faToken, setTemp2faToken] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const otpInputRefs = useRef([]);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // ─── Control de visualización de botón Microsoft SSO ───
  const [mostrarMicrosoftLogin, setMostrarMicrosoftLogin] = useState(false);

  useEffect(() => {
    const apiUrl = config.apiBaseUrl;
    fetch(`${apiUrl}/api/auth/config-publica`)
      .then((res) => res.json())
      .then((data) => {
        if (data && typeof data.mostrarMicrosoftLogin === 'boolean') {
          setMostrarMicrosoftLogin(data.mostrarMicrosoftLogin);
        }
      })
      .catch(() => {});
  }, []);

  const searchParams = new URLSearchParams(window.location.search);
  const ssoOverride = searchParams.get('sso') === '1' || searchParams.get('microsoft') === '1';
  const shouldShowMicrosoft = mostrarMicrosoftLogin || ssoOverride;

  const [companies, setCompanies] = useState([]);
  const [loggedUserDisplay, setLoggedUserDisplay] = useState('');
  const [defaultRoute, setDefaultRoute] = useState(
    () => sessionStorage.getItem('RutaInicial') || '/Solicitudes'
  );

  const companiesToShow = useMemo(() => {
    if (!Array.isArray(companies)) return [];
    if (companies.length > 1) {
      return [
        { EMPRESAID: 'ALL', NOMBRE_EMPRESA: 'Todas las empresas' },
        ...companies,
      ];
    }
    return companies;
  }, [companies]);

  const processSuccessfulLogin = useCallback((userData, displayName) => {
    const rutaInicial = userData.rutaInicial || sessionStorage.getItem('RutaInicial') || '/Solicitudes';

    setDefaultRoute(rutaInicial);
    sessionStorage.setItem('RutaInicial', rutaInicial);

    const empresas = Array.isArray(userData.empresas)
      ? userData.empresas
        .filter((e) => e && e.EMPRESAID != null)
        .map((e) => ({
          ...e,
          EMPRESAID: Number(e.EMPRESAID),
        }))
      : [];

    setCompanies(empresas);
    setLoggedUserDisplay(displayName || username.trim());

    if (empresas.length === 1) {
      const unica = empresas[0];
      const id = Number(unica.EMPRESAID);

      sessionStorage.setItem('EmpresaId', JSON.stringify([id]));
      sessionStorage.setItem(
        'EmpresaNombre',
        unica.NOMBRE_EMPRESA || unica.nombre || ''
      );

      navigate(rutaInicial, { replace: true });
      return;
    }

    if (empresas.length > 1) {
      setViewMode('COMPANIES');
      setIsSubmitting(false);
      setIsMsLoading(false);
      return;
    }

    navigate(rutaInicial, { replace: true });
  }, [navigate, username]);

  // 🔄 Procesar inicio de sesión cuando MSAL resuelve una cuenta (SOLO si Microsoft Login está permitido)
  useEffect(() => {
    let isMounted = true;
    if (!shouldShowMicrosoft) {
      setIsMsLoading(false);
      return;
    }

    const account = instance.getActiveAccount() || (accounts && accounts[0]);

    if (account && sessionStorage.getItem('isAuthenticated') !== 'true' && viewMode === 'LOGIN') {
      const email =
        account.username ||
        account.idTokenClaims?.email ||
        account.idTokenClaims?.preferred_username;
      const name = account.name;

      if (email) {
        setIsMsLoading(true);
        loginWithMicrosoft(email, name)
          .then((userData) => {
            if (isMounted) processSuccessfulLogin(userData, name || email);
          })
          .catch((err) => {
            console.error('Error autenticando con Microsoft:', err);
            if (isMounted) {
              setErrorMessage(err.message || 'Error al autenticar con Microsoft');
              setErrorModalOpen(true);
              try {
                instance.setActiveAccount(null);
              } catch (e) {}
            }
          })
          .finally(() => {
            if (isMounted) setIsMsLoading(false);
          });
      }
    }

    return () => {
      isMounted = false;
    };
  }, [accounts, instance, loginWithMicrosoft, processSuccessfulLogin, viewMode, shouldShowMicrosoft]);

  const handleMicrosoftLogin = async () => {
    setIsMsLoading(true);
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('[Microsoft Login Error]:', err);
      setIsMsLoading(false);
      if (err.errorCode !== 'user_cancelled') {
        setErrorMessage(err.message || 'Ocurrió un error al conectar con Microsoft.');
        setErrorModalOpen(true);
      }
    }
  };

  const handleLogin = async () => {
    const cleanedUsername = username.trim();
    const cleanedPassword = password.trim();

    if (!cleanedUsername || !cleanedPassword) {
      setErrorMessage('Por favor ingresa usuario y contraseña.');
      setErrorModalOpen(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await login(cleanedUsername, cleanedPassword);

      if (res?.require2fa) {
        // Se requiere ingresar el código 2FA
        setTemp2faToken(res.tempToken || '');
        setMaskedEmail(res.emailEnmascarado || '');
        setOtpDigits(['', '', '', '', '', '']);
        setViewMode('2FA');
        return;
      }

      processSuccessfulLogin(res, cleanedUsername);
    } catch (error) {
      setErrorMessage(error.message || 'Error al iniciar sesión');
      setErrorModalOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Manejo de verificación 2FA (6 Casillas OTP) ───
  const triggerVerify2fa = useCallback(async (codeToVerify) => {
    const code = String(codeToVerify || otpDigits.join('')).trim();
    if (!code || code.length < 6) {
      setErrorMessage('Por favor ingresa el código de 6 dígitos completo.');
      setErrorModalOpen(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const userData = await verify2fa(temp2faToken, code, username.trim());
      processSuccessfulLogin(userData, username.trim());
    } catch (error) {
      setErrorMessage(error.message || 'Código de verificación incorrecto o expirado.');
      setErrorModalOpen(true);
      setOtpDigits(['', '', '', '', '', '']);
      setTimeout(() => otpInputRefs.current[0]?.focus(), 150);
    } finally {
      setIsSubmitting(false);
    }
  }, [otpDigits, temp2faToken, username, verify2fa, processSuccessfulLogin]);

  const handleVerify2fa = (e) => {
    if (e) e.preventDefault();
    triggerVerify2fa(otpDigits.join(''));
  };

  const handleOtpChange = (index, e) => {
    const rawVal = e.target.value;
    const numericVal = rawVal.replace(/\D/g, '');

    if (!numericVal) {
      const newDigits = [...otpDigits];
      newDigits[index] = '';
      setOtpDigits(newDigits);
      return;
    }

    const lastDigit = numericVal.slice(-1);
    const newDigits = [...otpDigits];
    newDigits[index] = lastDigit;
    setOtpDigits(newDigits);

    // Auto-avance al siguiente input
    if (index < 5 && lastDigit) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // Auto-verificar si completó los 6 dígitos
    const fullCode = newDigits.join('');
    if (fullCode.length === 6 && !newDigits.includes('')) {
      triggerVerify2fa(fullCode);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!otpDigits[index] && index > 0) {
        const newDigits = [...otpDigits];
        newDigits[index - 1] = '';
        setOtpDigits(newDigits);
        otpInputRefs.current[index - 1]?.focus();
      } else {
        const newDigits = [...otpDigits];
        newDigits[index] = '';
        setOtpDigits(newDigits);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) {
      const newDigits = ['', '', '', '', '', ''];
      pasted.split('').forEach((char, idx) => {
        if (idx < 6) newDigits[idx] = char;
      });
      setOtpDigits(newDigits);
      const nextIdx = Math.min(pasted.length, 5);
      otpInputRefs.current[nextIdx]?.focus();

      if (pasted.length === 6) {
        triggerVerify2fa(pasted);
      }
    }
  };

  const handleResendOtp = async () => {
    setIsResendingOtp(true);
    setResendSuccess(false);
    try {
      const res = await resend2fa(temp2faToken, username.trim());
      if (res?.tempToken) {
        setTemp2faToken(res.tempToken);
      }
      setOtpDigits(['', '', '', '', '', '']);
      setTimeout(() => otpInputRefs.current[0]?.focus(), 150);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (error) {
      setErrorMessage(error.message || 'Error al reenviar el código.');
      setErrorModalOpen(true);
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleBackToLogin = () => {
    setViewMode('LOGIN');
    setOtpDigits(['', '', '', '', '', '']);
    setTemp2faToken('');
    setCompanies([]);
    sessionStorage.clear();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleLogin();
  };

  const handleSelectCompany = (empresaId) => {
    if (empresaId === 'ALL') {
      const allIds = companies.map((c) => Number(c.EMPRESAID)).filter(Boolean);
      sessionStorage.setItem('EmpresaId', JSON.stringify(allIds));
      sessionStorage.setItem('EmpresaNombre', 'Todas las empresas');
    } else {
      const single = companies.find((c) => Number(c.EMPRESAID) === Number(empresaId));
      sessionStorage.setItem('EmpresaId', JSON.stringify([Number(empresaId)]));
      sessionStorage.setItem(
        'EmpresaNombre',
        single?.NOMBRE_EMPRESA || single?.nombre || ''
      );
    }

    const targetRoute = defaultRoute || sessionStorage.getItem('RutaInicial') || '/Solicitudes';
    navigate(targetRoute, { replace: true });
  };

  return (
    <MyContainer>
      <MyPaper elevation={6}>
        {viewMode === '2FA' ? (
          /* ── PANTALLA DE CÓDIGO DE VERIFICACIÓN 2FA ── */
          <Box
            component="form"
            onSubmit={handleVerify2fa}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              animation: `${smoothFadeIn} 0.25s ease-out`,
            }}
          >
            <IconButton
              size="small"
              onClick={handleBackToLogin}
              sx={{ position: 'absolute', top: 14, left: 14, color: '#64748b' }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>

            <Box
              sx={{
                width: 'clamp(44px, 5.5vh, 52px)',
                height: 'clamp(44px, 5.5vh, 52px)',
                borderRadius: '14px',
                bgcolor: '#eff6ff',
                color: '#0655A5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1.5,
                boxShadow: '0 4px 12px rgba(6, 85, 165, 0.12)',
              }}
            >
              <MarkEmailReadOutlinedIcon sx={{ fontSize: 'clamp(22px, 3vh, 28px)' }} />
            </Box>

            <Typography
              component="h1"
              variant="h6"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                letterSpacing: '-0.02em',
                mb: 0.3,
                fontSize: 'clamp(1rem, 2vh, 1.25rem)',
                textAlign: 'center',
              }}
            >
              Código de Verificación
            </Typography>

            <Typography
              variant="body2"
              align="center"
              sx={{
                color: '#64748b',
                mb: 'clamp(12px, 2vh, 18px)',
                fontWeight: 500,
                fontSize: 'clamp(0.70rem, 1.1vh, 0.78rem)',
                lineHeight: 1.4,
              }}
            >
              Enviamos un código de 6 dígitos a:<br />
              <strong style={{ color: '#0655A5' }}>{maskedEmail || 'tu correo'}</strong>
            </Typography>

            {/* ── 6 CASILLAS INDIVIDUALES OTP ── */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 'clamp(5px, 1.2vw, 8px)',
                width: '100%',
                mb: 'clamp(14px, 2.2vh, 20px)',
              }}
              onPaste={handleOtpPaste}
            >
              {otpDigits.map((digit, idx) => {
                const isFilled = digit !== '';
                return (
                  <Box
                    key={idx}
                    component="input"
                    ref={(el) => (otpInputRefs.current[idx] = el)}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(idx, e)}
                    onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                    onFocus={(e) => e.target.select()}
                    disabled={isSubmitting}
                    sx={{
                      width: 'clamp(38px, 4.4vw, 44px)',
                      height: 'clamp(44px, 5.5vh, 52px)',
                      borderRadius: '12px',
                      border: isFilled ? '2px solid #0655A5' : '1.5px solid #cbd5e1',
                      bgcolor: isFilled ? '#f0f7ff' : '#ffffff',
                      color: '#0655A5',
                      textAlign: 'center',
                      fontSize: 'clamp(1.2rem, 2.3vh, 1.45rem)',
                      fontWeight: 800,
                      fontFamily: "'Geist', 'Inter', -apple-system, sans-serif",
                      outline: 'none',
                      boxShadow: isFilled
                        ? '0 2px 8px rgba(6, 85, 165, 0.15)'
                        : '0 1px 3px rgba(0, 0, 0, 0.04)',
                      transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:focus': {
                        borderColor: '#0655A5',
                        borderWidth: '2px',
                        bgcolor: '#ffffff',
                        boxShadow: '0 0 0 3.5px rgba(6, 85, 165, 0.18)',
                        transform: 'translateY(-2px)',
                      },
                      '&:disabled': {
                        bgcolor: '#f1f5f9',
                        color: '#94a3b8',
                        borderColor: '#e2e8f0',
                        cursor: 'not-allowed',
                      },
                    }}
                  />
                );
              })}
            </Box>

            <PrimaryButton 
              type="submit" 
              disabled={isSubmitting || otpDigits.join('').length < 6}
              sx={{
                opacity: otpDigits.join('').length === 6 ? 1 : 0.65,
                cursor: otpDigits.join('').length === 6 ? 'pointer' : 'default',
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} sx={{ color: '#ffffff' }} />
              ) : (
                'Verificar y Entrar'
              )}
            </PrimaryButton>

            {/* Reenviar código */}
            <Box sx={{ mt: 1.8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              {resendSuccess && (
                <Typography sx={{ fontSize: '0.68rem', color: '#16a34a', fontWeight: 700 }}>
                  ✓ Nuevo código enviado con éxito
                </Typography>
              )}
              <Button
                size="small"
                onClick={handleResendOtp}
                disabled={isResendingOtp || isSubmitting}
                startIcon={isResendingOtp ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  color: '#64748b',
                  fontWeight: 600,
                  '&:hover': { color: '#0655A5' },
                }}
              >
                {isResendingOtp ? 'Reenviando...' : '¿No recibiste el código? Reenviar'}
              </Button>
            </Box>
          </Box>
        ) : viewMode === 'LOGIN' ? (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >

            {/* ── ÍCONO CANDADO REDONDO ── */}
            <Box
              sx={{
                width: 'clamp(44px, 5.5vh, 52px)',
                height: 'clamp(44px, 5.5vh, 52px)',
                borderRadius: '50%',
                bgcolor: '#0655A5',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 'clamp(10px, 1.8vh, 16px)',
                boxShadow: '0 8px 20px rgba(6, 85, 165, 0.35)',
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 'clamp(22px, 2.8vh, 28px)', color: '#ffffff' }} />
            </Box>

            {/* ── ENCABEZADO CON CLAMP ── */}
            <Typography
              component="h1"
              variant="h5"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                letterSpacing: '-0.02em',
                mb: 0.3,
                fontSize: 'clamp(1.1rem, 2.2vh, 1.35rem)',
                textAlign: 'center',
              }}
            >
              Sistema de Pagos
            </Typography>
            <Typography
              variant="body2"
              align="center"
              sx={{
                color: '#64748b',
                mb: 'clamp(14px, 2.2vh, 22px)',
                fontWeight: 500,
                fontSize: 'clamp(0.74rem, 1.2vh, 0.84rem)',
              }}
            >
              Ingresa tus credenciales para acceder
            </Typography>

            {/* ── BOTÓN OFICIAL MICROSOFT SSO (CONTROLADO POR SWITCH) ── */}
            {shouldShowMicrosoft && (
              <>
                <MicrosoftButton
                  onClick={handleMicrosoftLogin}
                  disabled={isSubmitting || isMsLoading}
                >
                  {isMsLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <MicrosoftLogo />
                  )}
                  <span>
                    {isMsLoading ? 'Conectando con Microsoft...' : 'Iniciar sesión con Microsoft'}
                  </span>
                </MicrosoftButton>

                {/* ── DIVISOR ── */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    my: 'clamp(6px, 1.2vh, 12px)',
                  }}
                >
                  <Divider sx={{ flex: 1, borderColor: '#e2e8f0' }} />
                  <Typography
                    sx={{
                      px: 1,
                      fontSize: 'clamp(0.58rem, 0.9vh, 0.65rem)',
                      color: '#94a3b8',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    O CON USUARIO Y CONTRASEÑA
                  </Typography>
                  <Divider sx={{ flex: 1, borderColor: '#e2e8f0' }} />
                </Box>
              </>
            )}

            {/* ── INPUTS ADAPTABLES ── */}
            <StyledTextField
              label="Usuario"
              placeholder="Ingresa tu usuario"
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isSubmitting || isMsLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ color: '#0655A5', fontSize: 'clamp(16px, 1.8vh, 19px)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 'clamp(10px, 1.6vh, 16px)' }}
            />

            <StyledTextField
              label="Contraseña"
              placeholder="Ingresa tu contraseña"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting || isMsLoading}
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#0655A5', fontSize: 'clamp(16px, 1.8vh, 19px)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="alternar visibilidad de contraseña"
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 'clamp(12px, 2vh, 18px)' }}
            />

            <PrimaryButton type="submit" disabled={isSubmitting || isMsLoading}>
              {isSubmitting ? (
                <CircularProgress size={20} sx={{ color: '#ffffff' }} />
              ) : (
                'Iniciar Sesión'
              )}
            </PrimaryButton>
          </Box>
        ) : (
          /* ── SELECTOR DE EMPRESAS FLUIDO ── */
          <Box
            sx={{
              textAlign: 'center',
              width: '100%',
              animation: `${smoothFadeIn} 0.25s ease-out`,
            }}
          >
            <IconButton
              size="small"
              onClick={handleBackToLogin}
              sx={{ position: 'absolute', top: 16, left: 16, color: '#64748b' }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>

            <Box
              sx={{
                width: 'clamp(40px, 5vh, 48px)',
                height: 'clamp(40px, 5vh, 48px)',
                borderRadius: '14px',
                bgcolor: '#0655A5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1,
                boxShadow: '0 6px 14px rgba(6, 85, 165, 0.25)',
              }}
            >
              <BusinessIcon sx={{ fontSize: 'clamp(18px, 2.5vh, 24px)', color: '#ffffff' }} />
            </Box>

            <Typography
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                mb: 0.2,
                fontSize: 'clamp(0.85rem, 1.4vh, 1rem)',
              }}
            >
              Bienvenido, {loggedUserDisplay}
            </Typography>
            <Typography
              sx={{
                color: '#64748b',
                mb: 'clamp(10px, 1.6vh, 16px)',
                fontWeight: 500,
                fontSize: 'clamp(0.68rem, 1vh, 0.76rem)',
              }}
            >
              Selecciona la empresa con la que deseas operar
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                maxHeight: 'clamp(180px, 32vh, 240px)',
                overflowY: 'auto',
                pr: 0.5,
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#cbd5e1',
                  borderRadius: '4px',
                },
              }}
            >
              {companiesToShow.map((emp, index) => {
                const isAll = emp.EMPRESAID === 'ALL';
                return (
                  <Paper
                    key={emp.EMPRESAID}
                    elevation={0}
                    onClick={() => handleSelectCompany(emp.EMPRESAID)}
                    sx={{
                      p: { xs: '12px 14px', sm: '10px 14px' },
                      minHeight: { xs: '48px', sm: '42px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderRadius: '14px',
                      border: '1.5px solid #e2e8f0',
                      backgroundColor: isAll ? '#f0fdf4' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.18s ease',
                      animation: `${smoothFadeIn} ${0.15 + index * 0.05}s ease-out`,
                      '&:hover': {
                        borderColor: isAll ? '#22c55e' : '#0655A5',
                        backgroundColor: isAll ? '#dcfce7' : '#f8fafc',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      },
                      '&:active': {
                        transform: 'scale(0.98)',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                      <Box
                        sx={{
                          width: { xs: 32, sm: 28 },
                          height: { xs: 32, sm: 28 },
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: isAll ? '#dcfce7' : '#e6f0fa',
                          color: isAll ? '#16a34a' : '#0655A5',
                          flexShrink: 0,
                        }}
                      >
                        {isAll ? <DomainIcon sx={{ fontSize: { xs: 18, sm: 15 } }} /> : <BusinessIcon sx={{ fontSize: { xs: 18, sm: 15 } }} />}
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: '#0f172a',
                          fontSize: 'clamp(0.70rem, 1.1vh, 0.78rem)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          textAlign: 'left',
                        }}
                      >
                        {emp.NOMBRE_EMPRESA || emp.nombre}
                      </Typography>
                    </Box>
                    <Chip
                      label={isAll ? 'Todas' : 'Entrar'}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.62rem',
                        height: 20,
                        backgroundColor: isAll ? '#bbf7d0' : '#e2e8f0',
                        color: isAll ? '#15803d' : '#475569',
                      }}
                    />
                  </Paper>
                );
              })}
            </Box>
          </Box>
        )}
      </MyPaper>

      <ErrorModal
        open={errorModalOpen}
        handleClose={() => setErrorModalOpen(false)}
        errorMessage={errorMessage}
      />
    </MyContainer>
  );
}
